<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>{{ modalTitle }}</h2>
        <button @click="$emit('close')" class="btn-close">
          <LineIcon name="x" :size="15" :stroke-width="1.8" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div class="form-group">
          <label for="vendor_name">{{ $t('modals.vendorName') }} <span class="required">*</span></label>
          <input
            id="vendor_name"
            v-model="form.vendor_name"
            type="text"
            :placeholder="$t('modals.enterVendorName')"
            required
          />
        </div>

        <div class="form-group">
          <label for="vendor_code">{{ $t('modals.vendorCode') }}</label>
          <input
            id="vendor_code"
            v-model="form.vendor_code"
            type="text"
            :placeholder="$t('modals.enterVendorCode')"
          />
          <p class="field-hint">{{ $t('modals.vendorCodeHint') }}</p>
        </div>

        <div class="form-group">
          <label for="alias_names">{{ $t('modals.aliasNames') }}</label>
          <div class="alias-names-container">
            <div class="alias-tags">
              <span
                v-for="(alias, index) in form.alias_names"
                :key="index"
                class="alias-tag"
              >
                {{ alias }}
                <button
                  type="button"
                  @click="removeAlias(index)"
                  class="alias-remove"
                >
                  ×
                </button>
              </span>
              <input
                v-model="newAlias"
                @keydown.enter.prevent="addAlias"
                @keydown.,="addAlias"
                type="text"
                class="alias-input"
                :placeholder="$t('modals.addAliasPlaceholder')"
              />
            </div>
            <p class="field-hint">{{ $t('modals.aliasHint') }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="vendor_vat">{{ $t('modals.vatNumber') }}</label>
          <input
            id="vendor_vat"
            v-model="form.vendor_vat"
            type="text"
            placeholder="BG123456789"
          />
        </div>

        <div class="form-group">
          <label for="vendor_tax_id">{{ $t('modals.taxIdNumber') }}</label>
          <input
            id="vendor_tax_id"
            v-model="form.vendor_tax_id"
            type="text"
            :placeholder="$t('modals.enterTaxId')"
          />
          <p class="field-hint">{{ $t('modals.taxIdHint') }}</p>
        </div>

        <div class="form-group">
          <label for="vendor_address">{{ $t('modals.address') }}</label>
          <input
            id="vendor_address"
            v-model="form.vendor_address"
            type="text"
            :placeholder="$t('modals.enterAddress')"
          />
        </div>

        <div class="form-group form-group-toggle">
          <label class="toggle">
            <input type="checkbox" v-model="form.is_active" />
            <span class="toggle-track"></span>
          </label>
          <span class="toggle-text">{{ $t('modals.active') }}</span>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">
            {{ $t('modals.cancel') }}
          </button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saveButtonText }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';
import { useImageResize } from '@/composables/useImageResize';

const { t } = useI18n();

const props = defineProps({
  vendor: {
    type: Object,
    default: null
  },
  companyId: {
    type: [String, Number],
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const { resizeImage } = useImageResize();

const modalTitle = computed(() => {
  return props.vendor ? t('modals.editVendor') : t('modals.addVendor');
});

const saveButtonText = computed(() => {
  return saving.value ? t('modals.saving') : t('modals.saveVendor');
});

const form = ref({
  vendor_name: '',
  vendor_code: '',
  alias_names: [],
  vendor_vat: '',
  vendor_tax_id: '',
  vendor_address: '',
  vendor_email: '',
  vendor_phone: '',
  is_active: true
});

const saving = ref(false);
const extracting = ref(false);
const errorMessage = ref('');
const uploadedFile = ref(null);
const fileInput = ref(null);
const isDragging = ref(false);
const newAlias = ref('');
let dragCounter = 0;

// Load vendor data if editing
watch(() => props.vendor, (vendor) => {
  if (vendor) {
    form.value = {
      ...vendor,
      alias_names: Array.isArray(vendor.alias_names) ? vendor.alias_names : [],
      vendor_tax_id: vendor.vendor_tax_id || '',
      vendor_vat: vendor.vendor_vat || ''
    };
  } else {
    // Reset form when creating new vendor
    form.value = {
      vendor_name: '',
      vendor_code: '',
      alias_names: [],
      vendor_vat: '',
      vendor_tax_id: '',
      vendor_address: '',
      vendor_email: '',
      vendor_phone: '',
      is_active: true
    };
  }
}, { immediate: true });

const addAlias = () => {
  const alias = newAlias.value.trim();
  if (alias && !form.value.alias_names.includes(alias)) {
    form.value.alias_names.push(alias);
    newAlias.value = '';
  }
};

const removeAlias = (index) => {
  form.value.alias_names.splice(index, 1);
};

const handleSubmit = async () => {
  try {
    saving.value = true;
    errorMessage.value = '';

    console.log('Submitting vendor form data:', form.value);
    console.log('Alias names:', form.value.alias_names);
    console.log('Props vendor:', props.vendor);

    if (props.vendor && props.vendor.id) {
      // Update existing vendor
      console.log('Updating vendor with ID:', props.vendor.id);
      await api.put(`/api/companies/${props.companyId}/vendors/${props.vendor.id}`, form.value);
    } else {
      // Create new vendor
      console.log('Creating new vendor for company:', props.companyId);
      await api.post(`/api/companies/${props.companyId}/vendors`, form.value);
    }

    emit('saved');
  } catch (error) {
    console.error('Failed to save vendor:', error);
    console.error('Error details:', error.response?.data || error.message);
    
    // Display specific error message for vendor_code
    if (error.response?.data?.errors?.vendor_code) {
      errorMessage.value = error.response.data.errors.vendor_code[0];
    } else if (error.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else {
      errorMessage.value = `Failed to save vendor: ${error.message}`;
    }
  } finally {
    saving.value = false;
  }
};

const handleOverlayClick = () => {
  emit('close');
};

const handleFileSelect = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  await extractVendorData(file);
};

const openCamera = () => {
  if (fileInput.value) {
    fileInput.value.setAttribute('capture', 'environment');
    fileInput.value.click();
  }
};

const extractVendorData = async (file) => {
  try {
    extracting.value = true;
    uploadedFile.value = file;

    // Resize image before uploading to ensure optimal AI processing
    const resizedFile = await resizeImage(file);

    const formData = new FormData();
    formData.append('file', resizedFile);

    const response = await api.post(`/api/companies/${props.companyId}/vendors/extract`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    const data = response.data;

    // Auto-populate form fields from extracted data
    if (data.vendor_name) form.value.vendor_name = data.vendor_name;
    if (data.vendor_vat) form.value.vendor_vat = data.vendor_vat;
    if (data.vendor_address) form.value.vendor_address = data.vendor_address;
    if (data.vendor_email) form.value.vendor_email = data.vendor_email;
    if (data.vendor_phone) form.value.vendor_phone = data.vendor_phone;

  } catch (error) {
    console.error('Failed to extract vendor data:', error);
    alert('Failed to extract vendor information. Please enter details manually.');
  } finally {
    extracting.value = false;
  }
};

const clearUpload = () => {
  uploadedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const handleDragEnter = (e) => {
  dragCounter++;
  isDragging.value = true;
};

const handleDragOver = (e) => {
  e.dataTransfer.dropEffect = 'copy';
};

const handleDragLeave = (e) => {
  dragCounter--;
  if (dragCounter === 0) {
    isDragging.value = false;
  }
};

const handleDrop = async (e) => {
  dragCounter = 0;
  isDragging.value = false;

  const files = e.dataTransfer.files;
  if (files.length > 0) {
    await extractVendorData(files[0]);
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.btn-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: var(--danger);
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"] {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-light);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* iOS-style toggle, matches the rest of the app */
.form-group-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  margin: 0;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.2s ease;
  cursor: pointer;
}

.toggle-track::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle input:checked + .toggle-track {
  background: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  transform: translateX(16px);
}

.toggle input:focus-visible + .toggle-track {
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.toggle-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.btn-secondary,
.btn-primary {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-gray);
}

.btn-primary {
  background: var(--primary);
  border: none;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Alias Names Styling */
.alias-names-container {
  width: 100%;
}

.alias-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: white;
  min-height: 42px;
  align-items: center;
}

.alias-tags:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.alias-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 500;
}

.alias-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.15s ease;
}

.alias-remove:hover {
  background: #dbeafe;
  color: #1e40af;
}

.alias-input {
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  padding: 4px 6px;
  font-size: 14px;
  color: var(--text-primary);
}

.alias-input::placeholder {
  color: var(--text-light);
}

.field-hint {
  margin: 6px 0 0 0;
  font-size: 12px;
  color: var(--text-secondary);
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Dropzone Section */
.dropzone {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 16px;
  padding: 48px 32px;
  margin-bottom: 32px;
  border: 3px dashed #0ea5e9;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dropzone.dragover {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: var(--primary);
  border-width: 3px;
  box-shadow: 0 8px 24px rgba(14, 165, 233, 0.2);
  transform: scale(1.02);
}

.dropzone.uploading {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #f59e0b;
  cursor: wait;
}

.dropzone.has-file {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-color: #10b981;
}

.dropzone-empty,
.dropzone-extracting,
.dropzone-success {
  text-align: center;
  width: 100%;
}

.upload-icon,
.success-icon {
  margin: 0 auto 20px;
  color: var(--primary);
}

.dropzone.has-file .success-icon {
  color: #10b981;
}

.dropzone h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.dropzone p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.dropzone-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;
}

.btn-browse,
.btn-camera {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: white;
  border: 2px solid var(--primary);
  border-radius: 10px;
  color: var(--primary);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.1);
}

.btn-browse:hover,
.btn-camera:hover {
  background: var(--primary);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
}

.btn-browse:active,
.btn-camera:active {
  transform: translateY(0);
}

.btn-upload-another {
  margin-top: 16px;
  padding: 10px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-upload-another:hover {
  background: #0284c7;
  transform: translateY(-1px);
}

.file-types {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 16px;
}

.dropzone-extracting .spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #fde68a;
  border-top-color: #f59e0b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.extracting-text {
  font-size: 16px;
  font-weight: 600;
  color: #f59e0b;
}

.file-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 16px 0 8px;
  word-break: break-word;
}

.success-message {
  font-size: 14px;
  font-weight: 500;
  color: #10b981;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Legacy Upload Section (can be removed if not used elsewhere) */
.upload-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 2px dashed #0ea5e9;
}

.upload-info {
  text-align: center;
  margin-bottom: 20px;
}

.upload-info svg {
  color: var(--primary);
  margin-bottom: 12px;
}

.upload-info h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.upload-info p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.upload-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-upload,
.btn-camera {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 2px solid var(--primary);
  border-radius: 8px;
  color: var(--primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-upload:hover:not(:disabled),
.btn-camera:hover:not(:disabled) {
  background: var(--primary);
  color: white;
}

.btn-upload:disabled,
.btn-camera:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.extracting-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  color: var(--primary);
  font-size: 14px;
  font-weight: 500;
}

.error-message {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #c33;
  font-size: 14px;
  line-height: 1.5;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e0f2fe;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.uploaded-file {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
}

.uploaded-file span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-clear {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-clear:hover {
  background: var(--bg-gray);
  color: var(--danger);
}

.divider {
  position: relative;
  text-align: center;
  margin: 24px 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--border);
}

.divider span {
  position: relative;
  display: inline-block;
  padding: 0 16px;
  background: white;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── The switch, the chips and the hints ───────────────────────────────
 *
 * The switch was a solid blue pill with a white knob — the loudest mark in a
 * card of hairlines — and the alternative names sat in blue-on-blue chips
 * with an 18px typographic cross. All three are now what the rest of the
 * product uses: a rail with a handle, a quiet chip with a hairline, and a
 * hint one step smaller than the field it explains. */
.toggle {
  width: 30px;
  height: 17px;
}

.toggle-track {
  background: transparent;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  box-shadow: none;
}

.toggle-track::before,
.toggle-track::after {
  content: '';
  position: absolute;
  height: 9px;
  width: 9px;
  left: 2.5px;
  right: auto;
  top: 50%;
  margin-top: -4.5px;
  background: #94a3b8;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: none;
}

.toggle-track::after {
  content: none;
}

.toggle input:checked + .toggle-track {
  background: transparent;
  border-color: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  background: var(--primary);
  transform: translateX(13px);
}

.toggle:hover .toggle-track {
  border-color: var(--primary);
}

.toggle-text,
.toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

/* An alternative name on file: a quiet chip, not a blue badge. */
.alias-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px 2px 8px;
  background: #fff;
  border: 1px solid #dde3ea;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.alias-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-size: 13px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}

.alias-remove:hover {
  background: transparent;
  color: #e04545;
  transform: translateY(-1px);
}

.alias-tags {
  gap: 5px;
}

.alias-input {
  font-size: 13px;
}

/* What a field means, a step below the field itself. */
.field-hint {
  font-size: 11px;
  line-height: 1.4;
  color: #94a3b8;
}

.form-group {
  margin-bottom: 10px;
}

.form-group label {
  margin-bottom: 3px;
}
</style>
